import { useLanguage } from './LanguageContext';
import React, { useEffect, useState } from 'react';
import { Bell, Check } from 'lucide-react';
import {
  requestNotificationPermission,
  onForegroundMessage,
  getPermissionStatus,
  isNotificationSupported,
  initializeMessaging
} from '../services/pushNotificationService';

interface PushNotificationHandlerProps {
  investorId: string;
  apiUrl: string;
  onNotificationReceived?: (payload: any) => void;
}

// Export the hook for use in other components
export const useNotificationPermission = (investorId: string, apiUrl: string) => {
  const [permissionStatus, setPermissionStatus] = useState<string>('default');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const status = getPermissionStatus();
    setPermissionStatus(status);

    if (status === 'granted') {
      initializeMessaging();
    }
  }, []);

  const requestPermission = async () => {
    setIsLoading(true);
    const token = await requestNotificationPermission(investorId, apiUrl);
    
    if (token) {
      setPermissionStatus('granted');
    } else {
      setPermissionStatus(Notification.permission);
    }
    setIsLoading(false);
    return !!token;
  };

  return {
    permissionStatus,
    isLoading,
    requestPermission,
    isSupported: isNotificationSupported(),
    needsPermission: permissionStatus === 'default' && isNotificationSupported()
  };
};

// Inline notification prompt component (for use inside notifications panel)
interface InlineNotificationPromptProps {
  investorId: string;
  apiUrl: string;
  onEnabled?: () => void;
}

export const InlineNotificationPrompt: React.FC<InlineNotificationPromptProps> = ({
  investorId,
  apiUrl,
  onEnabled
}) => {
  const { t } = useLanguage();
  const { permissionStatus, isLoading, requestPermission, isSupported, needsPermission } =
    useNotificationPermission(investorId, apiUrl);

  if (!isSupported || !needsPermission) {
    return null;
  }

  const handleEnable = async () => {
    const success = await requestPermission();
    if (success && onEnabled) {
      onEnabled();
    }
  };

  return (
    <div style={{
      padding: '12px 16px',
      background: 'linear-gradient(135deg, #FEF9E7 0%, #FDF6E3 100%)',
      borderBottom: '1px solid #f0e6c8',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    }}>
      <div style={{
        width: '36px',
        height: '36px',
        borderRadius: '8px',
        background: '#D6B160',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        <Bell size={18} color="#fff" />
      </div>
      
      <div style={{ flex: 1 }}>
        <p style={{ 
          margin: 0, 
          fontSize: '12px', 
          fontWeight: '600',
          color: '#333'
        }}>
          {t('pnhEnable')}
        </p>
        <p style={{ 
          margin: '2px 0 0', 
          fontSize: '11px', 
          color: '#666' 
        }}>
          {t('pnhEnableSub')}
        </p>
      </div>
      
      <button
        onClick={handleEnable}
        disabled={isLoading}
        style={{
          padding: '8px 14px',
          background: '#D6B160',
          color: '#000',
          border: 'none',
          borderRadius: '6px',
          fontSize: '12px',
          fontWeight: '600',
          cursor: isLoading ? 'wait' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          whiteSpace: 'nowrap'
        }}
      >
        {isLoading ? '...' : <><Check size={14} /> {t('pnhEnableBtn')}</>}
      </button>
    </div>
  );
};

// Main component - now just handles foreground messages
const PushNotificationHandler: React.FC<PushNotificationHandlerProps> = ({
  investorId,
  apiUrl,
  onNotificationReceived
}) => {
  useEffect(() => {
    const status = getPermissionStatus();

    if (status === 'granted') {
      initializeMessaging();
      onForegroundMessage((payload) => {
        if (onNotificationReceived) {
          onNotificationReceived(payload);
        }
      });
    }
  }, [investorId, onNotificationReceived]);

  // This component no longer renders anything visible
  // The prompt is now shown inside the notifications panel
  return null;
};

export default PushNotificationHandler;